/* eslint-disable consistent-return */
import { RoutePath } from 'constants/RoutePath';
import {
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { accountSelector, serviceSelector, userSelector } from 'store/auth/authSelectors';
import { historyStorage } from 'utils/createHistory';

const SUB_KEY = 'history_stack';
const EDIT_FORM_PATHS = [RoutePath.VIEW_EDIT];

export const useHistoryKey = () => {
  const user = useSelector(userSelector);
  const account = useSelector(accountSelector);
  const service = useSelector(serviceSelector);

  return useMemo(() => {
    if (!user || !account || !service) return undefined;
    return `${user.id}_${SUB_KEY}_${account.id}_${service.id}`;
  }, [user?.id, account?.id, service?.id]);
};

export const useHistoryStack = () => {
  const key = useHistoryKey();

  if (!key) {
    return {
      get: () => [],
      clear: () => undefined,
      peek: () => undefined,
      pop: () => undefined,
      backToIndex: () => undefined,
      backToPath: () => undefined,
      removeParams: () => undefined,
      removePath: () => undefined,
    };
  }

  return {
    get: () => historyStorage.read(key),
    clear: () => historyStorage.clear(key),
    peek: () => historyStorage.peek(key),
    pop: () => historyStorage?.pop(key),
    backToIndex: (index: number) => historyStorage?.jumpToIndex(key, index),
    backToPath: (path: string) => historyStorage?.jumpToPath(key, path),
    removeParams: (params: string[]) => historyStorage?.removeParams(key, params),
    removePath: (path: string) => historyStorage?.removePath(key, path),
  };
};

export const useHistoryStackTracker = () => {
  const location = useLocation();
  const key = useHistoryKey();

  const isPopRef = useRef(false);

  const isViewPage = useMemo(() => (
    location.pathname.includes(`${RoutePath.VIEW}/`)
  ), [location.pathname]);

  const isEditForm = useMemo(() => (
    EDIT_FORM_PATHS.some((path) => location.pathname.includes(path))
  ), [location.pathname]);

  useEffect(() => {
    if (!key) return;

    const handlePopState = () => {
      isPopRef.current = true;
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [key]);

  useEffect(() => {
    if (!key || isEditForm) return;
    if (!isViewPage) {
      historyStorage.clear(key);
      return;
    }

    const stack = historyStorage.read(key);
    const index = stack.lastIndexOf(location.pathname);

    if ((isPopRef.current || location.state?.back) && index !== -1) {
      historyStorage.jumpToPath(key, location.pathname);
    } else {
      historyStorage.push(key, location.pathname);
    }
    isPopRef.current = false;
    location.state = { back: false };
  }, [location.pathname, key, isViewPage, isEditForm]);
};
